import { useCallback, useRef, useState } from 'react';
import { TiptapOutputType } from '@amzn/awscat-editor';
import ConfirmationModalView from '../ConfirmationModalView';
import TitleInput from '../TitleInput';
import {
  IMAGE_ALT_TEXT_MAX_LENGTH,
  IMAGE_ALT_TEXT_MIN_LENGTH,
  isWithinRange,
} from '../InlineValidations';
import { useEntityConverter } from '../../v2-hooks/useEntityConverter';

export interface ImageAltEntitiesModalProps {
  /** Closes the modal without saving. */
  readonly onDismiss: () => void;
  /** Called with the edited alt text when the user saves. */
  readonly onSave: (value: TiptapOutputType) => void;
  /** Value to seed the editor with. */
  readonly initialValue?: TiptapOutputType;
}

/**
 * Modal for editing an image's alt text with entity references.
 * Mounted only while open, so the inner editor re-seeds on each open.
 */
export function ImageAltEntitiesModal({
  onDismiss,
  onSave,
  initialValue = '',
}: ImageAltEntitiesModalProps) {
  const entityConverter = useEntityConverter();

  const valueRef = useRef<TiptapOutputType>(initialValue);

  // Seed validity from the incoming value so a pre-existing out-of-bounds alt
  // keeps Save disabled even before the user touches the editor.
  const [isValid, setValid] = useState(() =>
    isWithinRange(
      entityConverter(initialValue),
      IMAGE_ALT_TEXT_MIN_LENGTH,
      IMAGE_ALT_TEXT_MAX_LENGTH,
    ),
  );

  // When the inner editor changes, remember the latest value and its validity.
  const handleTitleChanged = useCallback((value: TiptapOutputType, isValidationPassed: boolean) => {
    valueRef.current = value;
    setValid(isValidationPassed);
  }, []);

  const handleSave = useCallback(() => onSave(valueRef.current), [onSave]);

  return (
    <ConfirmationModalView
      header="Edit Alt Text"
      confirmLabel="Save"
      isConfirming={false}
      isConfirmDisabled={!isValid}
      onDismiss={onDismiss}
      onConfirm={handleSave}
      body={
        <TitleInput
          initialTitle={initialValue}
          label="Alt text with entities"
          description="Describe the image using entity references for downstream processing. Entities will be resolved to their current values at publish time."
          minLength={IMAGE_ALT_TEXT_MIN_LENGTH}
          maxLength={IMAGE_ALT_TEXT_MAX_LENGTH}
          autoFocus
          onTitleChanged={handleTitleChanged}
        />
      }
    />
  );
}
